import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { PatientSearchPicker } from "@/components/PatientSearchPicker";
import type { Patient } from "@/resources/types";

import { useExternalRegisterAdmin } from "./externalRegisterAdminProvider";
import { EntryForm } from "./ExternalRegisterEntryForm";
import { RelinkPatientSearch } from "./ExternalRegisterRelinkPatientSearch";
import {
  emptyEntryRecord,
  getPatientSummary,
  isEntryRecordValid,
  isNeverLinkedEntry,
  toEntryRecord,
  trimEntryRecord,
} from "./externalRegisterAdminHelpers";
import type {
  ExternalRegisterAdminActionDialogState,
  ExternalRegisterEntryRecord,
  ExternalRegisterMaintenanceEntry,
} from "./externalRegisterAdminTypes";
import {
  compactAdminTableSx,
  compactFieldSx,
  compactOutlinedButtonSx,
} from "./externalRegisterAdminUi";

type CreateEntryMode = "existing" | "minimal" | "unlinked";

const applyPatientIdentityToRecord = (
  record: ExternalRegisterEntryRecord,
  patient: Patient | null,
): ExternalRegisterEntryRecord => {
  if (!patient) return record;
  return {
    ...record,
    surname: patient.lastName || record.surname,
    givenName: patient.firstName || record.givenName,
    ...(patient.dateOfBirth && !record.dob ? { dob: patient.dateOfBirth } : {}),
    ...(patient.sex && !record.sex ? { sex: patient.sex } : {}),
    ...(patient.umrn && !record.umrn ? { umrn: patient.umrn } : {}),
  };
};

export const CreateEntryDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { createEntry, saving } = useExternalRegisterAdmin();
  const [mode, setMode] = useState<CreateEntryMode>("existing");
  const [patientId, setPatientId] = useState("");
  const [record, setRecord] = useState<ExternalRegisterEntryRecord>(emptyEntryRecord);

  useEffect(() => {
    if (!open) return;
    setMode("existing");
    setPatientId("");
    setRecord(emptyEntryRecord);
  }, [open]);

  const hasResolvedPatientStep = mode === "minimal" || mode === "unlinked" || Boolean(patientId.trim());
  const canSubmit = hasResolvedPatientStep && isEntryRecordValid(record);
  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("externalRegister.admin.dialogs.create.title")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined" sx={{ alignItems: "center" }}>
            {t("externalRegister.admin.dialogs.fieldGuidance")}
          </Alert>
          {saving ? (
            <Alert
              severity="info"
              variant="outlined"
              icon={<CircularProgress size={18} />}
              sx={{ alignItems: "center" }}
            >
              {t("externalRegister.admin.dialogs.create.savingMessage")}
            </Alert>
          ) : null}
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t("externalRegister.admin.dialogs.create.step1Title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("externalRegister.admin.dialogs.create.step1Description")}
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={mode}
            onChange={(_event, nextMode: CreateEntryMode | null) => {
              if (!nextMode || saving) return;
              setMode(nextMode);
              if (nextMode !== "existing") setPatientId("");
            }}
            sx={{
              "& .MuiToggleButton-root": {
                minHeight: 44,
                px: 1.25,
                textTransform: "none",
                fontWeight: 800,
                borderColor: "divider",
              },
              "& .MuiToggleButton-root.Mui-disabled": {
                borderColor: "divider",
              },
              "& .Mui-selected": {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
              "& .Mui-selected.Mui-disabled": {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                opacity: 0.7,
              },
            }}
          >
            <ToggleButton value="existing" disabled={saving}>
              {t("externalRegister.admin.dialogs.create.patientOptions.linkExisting")}
            </ToggleButton>
            <ToggleButton value="minimal" disabled={saving}>
              {t("externalRegister.admin.dialogs.create.patientOptions.createMinimal")}
            </ToggleButton>
            <ToggleButton value="unlinked" disabled={saving}>
              {t("externalRegister.admin.dialogs.create.patientOptions.holdWithoutLink")}
            </ToggleButton>
          </ToggleButtonGroup>
          {mode === "existing" ? (
            <PatientSearchPicker
              selectedPatientId={patientId}
              onSelectPatient={(nextPatientId, patient) => {
                setPatientId(nextPatientId);
                setRecord((currentRecord) => applyPatientIdentityToRecord(currentRecord, patient));
              }}
              label={t("externalRegister.admin.dialogs.create.patientSearch.label")}
              selectedLabel={t("externalRegister.admin.dialogs.create.patientSearch.selectedLabel")}
              ariaLabel={t("externalRegister.admin.dialogs.create.patientSearch.ariaLabel")}
              required
              disabled={saving}
              textFieldSx={compactFieldSx}
              selectButtonSx={compactOutlinedButtonSx}
            />
          ) : mode === "minimal" ? (
            <Alert severity="warning" variant="outlined" sx={{ alignItems: "center" }}>
              {t("externalRegister.admin.dialogs.create.minimalWarning")}
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined" sx={{ alignItems: "center" }}>
              {t("externalRegister.admin.dialogs.create.unlinkedWarning")}
            </Alert>
          )}
          <Divider />
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t("externalRegister.admin.dialogs.create.step2Title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasResolvedPatientStep
                ? t("externalRegister.admin.dialogs.create.step2CompleteFields")
                : t("externalRegister.admin.dialogs.create.step2SelectPatient")}
            </Typography>
          </Box>
          <Box sx={{
            opacity: hasResolvedPatientStep && !saving ? 1 : 0.55,
            pointerEvents: hasResolvedPatientStep && !saving ? "auto" : "none",
          }}>
            <EntryForm value={record} onChange={setRecord} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t("common.buttons.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClick={async () => {
            const entry = trimEntryRecord(record);
            try {
              await createEntry(mode === "existing"
                ? { patientId: patientId.trim(), entry }
                : mode === "minimal"
                  ? { createMinimalPatient: true, entry }
                  : { createUnlinkedEntry: true, entry });
              onClose();
            } catch {
              // Provider exposes a safe error message in the admin surface.
            }
          }}
        >
          {saving ? t("common.buttons.creating") : t("externalRegister.admin.dialogs.create.buttons.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const EditEntryDialog = ({
  entry,
  onClose,
}: {
  entry: ExternalRegisterMaintenanceEntry | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { updateEntry, saving } = useExternalRegisterAdmin();
  const [record, setRecord] = useState<ExternalRegisterEntryRecord>(emptyEntryRecord);

  useEffect(() => {
    if (!entry) return;
    setRecord(toEntryRecord(entry));
  }, [entry]);

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={Boolean(entry)} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("externalRegister.admin.dialogs.edit.title")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined" sx={{ alignItems: "center" }}>
            <Stack spacing={1}>
              <Typography variant="body2">
                {t("externalRegister.admin.dialogs.edit.alert")}
              </Typography>
              <Divider />
              <Typography variant="body2">
                {t("externalRegister.admin.dialogs.fieldGuidance")}
              </Typography>
            </Stack>
          </Alert>
          {saving ? (
            <Alert
              severity="info"
              variant="outlined"
              icon={<CircularProgress size={18} />}
              sx={{ alignItems: "center" }}
            >
              {t("externalRegister.admin.dialogs.edit.savingMessage")}
            </Alert>
          ) : null}
          <Box sx={{
            opacity: saving ? 0.55 : 1,
            pointerEvents: saving ? "none" : "auto",
          }}>
            <EntryForm value={record} onChange={setRecord} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t("common.buttons.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!entry || !isEntryRecordValid(record) || saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClick={async () => {
            if (!entry) return;
            try {
              await updateEntry(entry.id, trimEntryRecord(record));
              onClose();
            } catch {
              // Provider exposes a safe error message in the admin surface.
            }
          }}
        >
          {saving ? t("common.buttons.saving") : t("externalRegister.admin.dialogs.edit.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type ExternalRegisterMaintenanceAuditEvent = NonNullable<ExternalRegisterMaintenanceEntry["history"]>[number];

const formatAuditAction = (action: string, t: (key: string) => string): string => {
  if (action === "imported") return t("externalRegister.admin.dialogs.details.table.actionImported");
  return action.replace(/_/g, " ");
};

const formatAuditTimestamp = (timestamp: string | undefined, t: (key: string) => string): string => {
  if (!timestamp) return t("common.empty.notRecorded");
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return timestamp;

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(parsed);
};

const getAuditChangedFields = (changes: ExternalRegisterMaintenanceAuditEvent["changes"], t: (key: string) => string): string => {
  if (!changes || typeof changes !== "object") return t("externalRegister.admin.dialogs.details.table.fallbacks.noFieldDetail");
  const fields = Object.keys(changes);
  return fields.length ? fields.join(", ") : t("externalRegister.admin.dialogs.details.table.fallbacks.noFieldDetail");
};

export const EntryDetailsDialog = ({
  entry,
  onClose,
}: {
  entry: ExternalRegisterMaintenanceEntry | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={Boolean(entry)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("externalRegister.admin.dialogs.details.title")}</DialogTitle>
      <DialogContent dividers>
        {entry ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">{t("externalRegister.admin.dialogs.details.sections.comments")}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {entry.comments || t("externalRegister.admin.dialogs.details.empty.noComments")}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="overline" color="text.secondary">{t("externalRegister.admin.dialogs.details.sections.auditHistory")}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {t("externalRegister.admin.dialogs.details.importCaption")}
              </Typography>
              {entry.history?.length ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5, maxHeight: 280 }}>
                  <Table
                    stickyHeader
                    size="small"
                    sx={{
                      ...compactAdminTableSx,
                      tableLayout: "fixed",
                      "& .MuiTableCell-root": {
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 150 }}>{t("externalRegister.admin.dialogs.details.table.headers.action")}</TableCell>
                        <TableCell sx={{ width: 160 }}>{t("externalRegister.admin.dialogs.details.table.headers.time")}</TableCell>
                        <TableCell sx={{ width: 170 }}>{t("externalRegister.admin.dialogs.details.table.headers.user")}</TableCell>
                        <TableCell>{t("externalRegister.admin.dialogs.details.table.headers.reason")}</TableCell>
                        <TableCell>{t("externalRegister.admin.dialogs.details.table.headers.changedFields")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entry.history.map((event, index) => (
                        <TableRow key={`${event.timestamp}-${event.action}-${index}`}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 800, textTransform: "capitalize" }}>
                              {formatAuditAction(event.action, t)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" title={event.timestamp || undefined}>
                              {formatAuditTimestamp(event.timestamp, t)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {event.userName || event.userId || t("common.empty.notRecorded")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                              {event.reason || t("externalRegister.admin.dialogs.details.table.fallbacks.noReason")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                              {getAuditChangedFields(event.changes, t)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">{t("externalRegister.admin.dialogs.details.empty.noAudit")}</Typography>
              )}
            </Box>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export const DeleteNeverLinkedEntryDialog = ({
  entry,
  onClose,
}: {
  entry: ExternalRegisterMaintenanceEntry | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { deleteNeverLinkedEntry, saving } = useExternalRegisterAdmin();
  const canDelete = Boolean(entry && isNeverLinkedEntry(entry));
  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={Boolean(entry)} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("externalRegister.admin.dialogs.deleteNeverLinked.title")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined">
            {t("externalRegister.admin.dialogs.deleteNeverLinked.warning")}
          </Alert>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {entry ? getPatientSummary(entry) || entry.id : ""}
          </Typography>
          {!canDelete && entry ? (
            <Alert severity="error" variant="outlined">
              {t("externalRegister.admin.dialogs.deleteNeverLinked.hasLinkError")}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t("common.buttons.cancel")}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!entry || !canDelete || saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClick={async () => {
            if (!entry || !canDelete) return;
            try {
              await deleteNeverLinkedEntry(entry.id);
              onClose();
            } catch {
              // Provider exposes a safe error message in the admin surface.
            }
          }}
        >
          {t("externalRegister.admin.dialogs.deleteNeverLinked.buttons.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const EntryActionDialog = ({
  state,
  onClose,
}: {
  state: ExternalRegisterAdminActionDialogState;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const {
    inactivateEntry,
    reactivateEntry,
    unlinkEntry,
    relinkEntry,
  } = useExternalRegisterAdmin();
  const [reason, setReason] = useState("");
  const [targetPatientId, setTargetPatientId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!state) return;
    setReason("");
    setTargetPatientId("");
    setSubmitting(false);
  }, [state]);

  if (!state) return null;

  const requiresReason = state.kind === "unlink" || state.kind === "relink";
  const requiresPatient = state.kind === "relink";
  const canSubmit = Boolean((!requiresReason || reason.trim()) && (!requiresPatient || targetPatientId.trim()));
  const titleByKind: Record<NonNullable<ExternalRegisterAdminActionDialogState>["kind"], string> = {
    inactivate: t("externalRegister.admin.dialogs.entryAction.titles.inactivate"),
    reactivate: t("externalRegister.admin.dialogs.entryAction.titles.reactivate"),
    unlink: t("externalRegister.admin.dialogs.entryAction.titles.unlink"),
    relink: t("externalRegister.admin.dialogs.entryAction.titles.relink"),
  };
  const confirmByKind: Record<NonNullable<ExternalRegisterAdminActionDialogState>["kind"], string> = {
    inactivate: t("externalRegister.admin.dialogs.entryAction.buttons.confirmInactivate"),
    reactivate: t("externalRegister.admin.dialogs.entryAction.buttons.confirmReactivate"),
    unlink: t("externalRegister.admin.dialogs.entryAction.buttons.confirmUnlink"),
    relink: t("externalRegister.admin.dialogs.entryAction.buttons.confirmRelink"),
  };
  const handleClose = () => {
    if (!submitting) onClose();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (state.kind === "inactivate") await inactivateEntry(state.entry.id, reason);
      if (state.kind === "reactivate") await reactivateEntry(state.entry.id, reason);
      if (state.kind === "unlink") await unlinkEntry(state.entry.id, reason);
      if (state.kind === "relink") await relinkEntry(state.entry.id, targetPatientId, reason);
      onClose();
    } catch {
      // Provider exposes a safe error message in the admin surface.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{titleByKind[state.kind]}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined">
            <Typography component="span" variant="body2" sx={{ display: "block" }}>
              {t("externalRegister.admin.dialogs.entryAction.confirmMessage")}
            </Typography>
            <Typography component="span" variant="body2" sx={{ display: "block", fontWeight: 800 }}>
              {getPatientSummary(state.entry) || state.entry.id}
            </Typography>
          </Alert>
          {state.kind === "relink" ? (
            <RelinkPatientSearch selectedPatientId={targetPatientId} onSelectPatient={setTargetPatientId} disabled={submitting} />
          ) : null}
          <TextField
            size="small"
            label={requiresReason ? t("externalRegister.admin.dialogs.entryAction.reason.label") : t("externalRegister.admin.dialogs.entryAction.reason.labelOptional")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required={requiresReason}
            disabled={submitting}
            autoComplete="new-password"
            multiline
            minRows={3}
            inputProps={{
              "aria-label": t("externalRegister.admin.dialogs.entryAction.reason.ariaLabel"),
              autoComplete: "new-password",
              name: "external-register-entry-action-reason",
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>{t("common.buttons.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClick={submit}
        >
          {confirmByKind[state.kind]}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
